import {
  forwardRef,
  Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo
} from 'react'
import Input from 'core/components/inputs/Input'
import useCurrentTeam from 'core/hooks/team/useCurrentTeam'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useQueryCallbacks } from 'core/hooks/data/useQueryCallbacks'
import APIService from 'api/APIService'
import {
  ErrorMessage,
  ReportTiny,
  TransactionDetailed,
  TransactionShort
} from 'types/entity'
import useNotifications from 'core/hooks/ui/useNotifications'
import Button from 'core/components/buttons/Button'
import FilePicker from 'core/widgets/transaction-detail/FilePicker'
import { FormProvider, useForm } from 'react-hook-form'
import SmartField from 'core/components/form/SmartField'
import { LockFilledIcon } from 'core/_legacy/icons'
import Select from 'core/components/inputs/Select'
import usePurposes from 'core/hooks/settings/usePurposes'
import { getUniqueListBy } from 'utils/AppHelper'
import Status from 'core/widgets/status/Status'
import useRolesAndPermissions from 'core/hooks/user/useRolesAndPermissions'
import dayjs from 'utils/dayjs'
import CategoryOption from 'core/widgets/transaction-detail/CategoryOption'
import cn from 'utils/cn'
import numeral from 'numeral'
import PopConfirm from 'core/components/dialogs/PopConfirm'
import DatePicker from 'core/components/inputs/date-picker/DatePicker'
import useNavigation from 'core/hooks/navigation/useNavigation'
import Tooltip from 'core/components/dialogs/Tooltip'
import Icon from 'core/components/icons/Icon'
import { MenuProps } from 'core/components/dialogs/Menu'
import { getFileMimeType } from 'utils/Functions'
import AnalyticsHelper from 'utils/AnalyticsHelper'
import TagsInput from 'core/widgets/transaction-detail/TagsInput'

interface TransactionDetailProps {
  view: 'personal' | 'team'
  transactionId: TransactionDetailed['token_id']
  onError?: VoidFunction
  onSuccess?: (t?: TransactionShort) => void
  readOnly?: boolean // True by default - will be shown as a first stage only on pay periods
  enableStartDate?: string | null
  enableEndDate?: string | null
}

export interface FormValues {
  amount: string
  purpose: TransactionDetailed['purpose']
  merchant: TransactionDetailed['merchant']
  date: TransactionDetailed['date']
  notes: TransactionDetailed['notes']
  category: TransactionDetailed['category']
  tags: TransactionDetailed['tags']
  report_token_id: ReportTiny['token_id'] | Partial<ReportTiny> | null
  photo?: TransactionDetailed['photo']
  photo_attributes: File | null
}

export interface TransactionDetailRef {
  getFormDirty: () => boolean
}

const TransactionDetail = forwardRef(
  (
    {
      view,
      transactionId,
      onError,
      onSuccess,
      readOnly = false,
      enableStartDate,
      enableEndDate
    }: TransactionDetailProps,
    ref: Ref<TransactionDetailRef>
  ) => {
    const notifications = useNotifications()
    const { pathname } = useNavigation()
    const isPersonalReportPage =
      pathname.includes('reports') && !pathname.includes('team')
    const reportId = useMemo(() => {
      return pathname?.split('/')?.[2] ?? ''
    }, [pathname])

    const { data: team } = useCurrentTeam()
    const { categories_enabled, tags_enabled } = team ?? {}
    const queryClient = useQueryClient()
    const { allPurposes, teamPurposes, customIncomeSources } = usePurposes()
    const { hasTeam } = useRolesAndPermissions()
    const isNewTransaction = useMemo(
      () => String(transactionId).startsWith('new-id-'),
      [transactionId]
    )
    const methods = useForm<FormValues>({
      defaultValues: {
        amount: '$0.00',
        report_token_id:
          isNewTransaction && isPersonalReportPage ? reportId : undefined
      }
    })

    const {
      control,
      reset,
      setValue,
      watch,
      setError,
      formState: { isDirty },
      handleSubmit
    } = methods

    // Transaction detail drawer uses it to get the form state
    useImperativeHandle(
      ref,
      () => ({
        getFormDirty: () => isDirty
      }),
      [isDirty]
    )

    const { date, amount: formAmount, photo, report_token_id } = watch()
    const amount = formAmount ?? 0
    const amountNumber = formAmount
      ? parseFloat(formAmount?.replaceAll(/(\$|,)/g, ''))
      : 0

    const queryKey = useMemo(() => {
      return view === 'personal'
        ? ['transaction', 'detail', transactionId]
        : ['team', team?.token_id, 'transaction', transactionId]
    }, [team?.token_id, transactionId, view])

    const { data: transaction, isLoading } = useQueryCallbacks({
      queryKey: queryKey,
      queryFn: () => {
        return view === 'team'
          ? APIService.transactions.getReportTransaction({
              transactionId: transactionId,
              teamId: team?.token_id ?? ''
            })
          : APIService.transactions.getTransaction({
              transactionId: transactionId
            })
      },
      enabled:
        view === 'team'
          ? !!(transactionId && team?.token_id)
          : !!transactionId && !isNewTransaction,
      retry: false,
      onError: () => {
        onError?.()
        notifications.error(
          'Could not fetch transaction detail. Please try again later.'
        )
      }
    })

    const isReadOnly = readOnly || !!transaction?.is_locked || view === 'team'

    const { data: reports } = useQuery({
      queryKey: ['reports', { status: 'draft' }],
      queryFn: () =>
        APIService.reports.getReports({
          limit: 50
        }),
      enabled: !!transactionId,
      refetchOnWindowFocus: false
    })

    const categoryInputConfig = useMemo((): {
      show: boolean
      disabled: boolean
    } => {
      if (!hasTeam) {
        return {
          show: true,
          disabled: false
        }
      }

      return {
        show: categories_enabled ? true : !!transaction?.category,
        disabled: categories_enabled ? false : !!transaction?.category
      }
    }, [categories_enabled, hasTeam, transaction?.category])

    // eslint-disable-next-line
    const tagsInputConfig = useMemo((): {
      show: boolean
      disabled: boolean
    } => {
      if (!hasTeam) {
        return {
          show: true,
          disabled: false
        }
      }

      return {
        show: tags_enabled ? true : !!transaction?.tags.length,
        disabled: tags_enabled ? false : !!transaction?.tags?.length
      }
    }, [hasTeam, tags_enabled, transaction?.tags?.length])

    const filteredReports = useMemo(() => {
      return (
        reports?.filter((data) => {
          const { period_start_date, period_end_date, token_id, status } = data
          if ((!period_start_date || !period_end_date) && status === 'draft')
            return true
          if (
            !period_start_date ||
            !period_end_date ||
            !['draft', 'paid'].includes(status)
          )
            return false
          const periodStart = dayjs(period_start_date, 'M/D/YYYY')
          const periodEnd = dayjs(period_end_date, 'M/D/YYYY')
          return (
            dayjs(date).isBetween(periodStart, periodEnd, 'days', '[]') ||
            report_token_id === token_id
          )
        }) ?? []
      )
    }, [reports, report_token_id, date])

    const { isPending: updatingTransaction, mutate: updateTransaction } =
      useMutation({
        mutationFn: (formValues: FormData) => {
          return APIService.transactions.updateTransaction(
            transactionId,
            formValues
          )
        },
        onSuccess(data) {
          if (isPersonalReportPage && data.report?.token_id === reportId)
            queryClient.invalidateQueries({ queryKey: ['reports', reportId] })
          notifications.success(
            `${isExpense ? 'Expense' : 'Revenue'} has been updated successfully`
          )
          setValue('photo', data.photo)
          onSuccess?.(data)
        },
        onError: () => {
          notifications.error(
            `An error occurred while updating the ${isExpense ? 'expense' : 'revenue'}. Please try again.`
          )
        }
      })

    const { isPending: creatingTransaction, mutate: createTransaction } =
      useMutation({
        mutationFn: (formValues: FormData) => {
          return APIService.transactions.createTransaction(formValues)
        },
        onSuccess(data) {
          setValue('photo', data.photo)
          if (isPersonalReportPage && data.report?.token_id === reportId)
            queryClient.invalidateQueries({ queryKey: ['reports', reportId] })
          if (isExpense) {
            AnalyticsHelper.trackEvent('expense_created', {
              expense_category: data.category ?? '',
              currency: 'USD',
              amount: data.amount,
              is_revenue_event: false
            })
          }
          onSuccess?.(data)
          notifications.success(
            `${isExpense ? 'Expense' : 'Revenue'} has been created successfully`
          )
        },
        onError(error: Error) {
          const response = (error as ErrorMessage).response
          if (!response?.data) return
          const data = response.data as Record<string, string[]>
          Object.keys(data).forEach((key) => {
            setError(key as keyof FormValues, {
              message: data[key][0]
            })
          })
        }
      })

    const { mutate: removeFromReport } = useMutation({
      mutationKey: ['reports-detach', 'transactions'],
      mutationFn: (transactionId: TransactionShort['token_id']) =>
        APIService.reports.detachFromReport({
          reportId: String(
            transaction?.report?.token_id
          ) as ReportTiny['token_id'],
          kind: 'transactions',
          id: transactionId
        })
    })

    const { isPending: deletingTransaction, mutate: deleteTransaction } =
      useMutation({
        mutationFn: APIService.transactions.batchDeleteTransactions,
        onSuccess: () => {
          if (isPersonalReportPage)
            queryClient.invalidateQueries({ queryKey: ['reports', reportId] })
          notifications.success(
            `${isExpense ? 'Expense' : 'Revenue'} has been deleted successfully`
          )
          onSuccess?.()
        },
        onError: (err) => {
          console.error(err)
          notifications.error('Could not delete transaction')
        }
      })

    const onDelete = useCallback(() => {
      removeFromReport(transactionId)
      deleteTransaction({
        token_ids: [transactionId]
      })
    }, [deleteTransaction, transactionId, removeFromReport])

    const { title, entityName, isExpense } = useMemo(() => {
      const isExpense =
        (transaction?.amount ?? 0) < 0 ||
        transactionId === 'new-id-expense' ||
        view === 'team'
      return {
        isExpense,
        title: `${isNewTransaction ? 'New' : ''} ${
          isExpense ? 'Expense' : 'Revenue'
        } ${!isNewTransaction ? 'Details' : ''}`,
        entityName: isExpense ? 'expense' : 'revenue'
      }
    }, [transaction?.amount, transactionId, view, isNewTransaction])

    const prepareImageData = (data: FormValues, formData: FormData) => {
      if (data.photo_attributes === null && data.photo === null) {
        formData.append('transaction[photo_attributes][_destroy]', String(true))
      } else if (data.photo_attributes instanceof File) {
        const mimeType = getFileMimeType(data.photo_attributes)
        const blob = new Blob([data.photo_attributes])
        const fileOfBlob = new File([blob], data.photo_attributes.name, {
          type: mimeType
        })
        formData.append('transaction[photo_attributes][image]', fileOfBlob)
      }
    }

    const prepareTagsData = (data: FormValues, formData: FormData) => {
      if (data.tags?.length) {
        data.tags.forEach((val) => {
          formData.append(`transaction[tags][]`, String(val))
        })
      }
      if (!data.tags?.length) {
        formData.append(`transaction[tags][]`, '')
      }
    }

    const onSubmit = useCallback(
      async (data: FormValues) => {
        if (!data.date) {
          setError(
            'date',
            { message: 'Date is required' },
            { shouldFocus: true }
          )
          notifications.error('Date is required')
          return
        }
        const formData = new FormData()
        const keys = Object.keys(data).filter(
          (key) =>
            ![
              'amount',
              'photo_attributes',
              'photo',
              'tags',
              'purpose'
            ].includes(key)
        )

        if (
          !data.report_token_id &&
          !isNewTransaction &&
          transaction?.report?.token_id
        ) {
          await removeFromReport(transactionId)
        }

        if (customIncomeSources.includes(data.purpose)) {
          formData.append('transaction[income_source]', data.purpose)
          formData.append('transaction[purpose]', 'Work')
        } else {
          // eslint-disable-next-line
          // @ts-ignore
          formData.append('transaction[income_source]', null)
          formData.append('transaction[purpose]', data.purpose)
        }

        if (!data.notes) {
          formData.append('transaction[notes]', '')
        }

        const amount = parseFloat(data.amount?.replaceAll(/(\$|,)/g, ''))
        if (!isNaN(amount)) {
          formData.append(
            'transaction[amount]',
            String(isExpense ? amount * -1 : amount)
          )
        }
        prepareImageData(data, formData)
        prepareTagsData(data, formData)

        for (const key of keys) {
          const item = data[key as keyof FormValues]
          if (item) {
            // Only assign properties with values
            formData.append('transaction[' + key + ']', String(item))
          }
        }

        isNewTransaction
          ? await createTransaction(formData)
          : await updateTransaction(formData)
      },
      [
        isNewTransaction,
        transaction?.report?.token_id,
        customIncomeSources,
        createTransaction,
        updateTransaction,
        setError,
        notifications,
        removeFromReport,
        transactionId,
        isExpense
      ]
    )

    const reportOptions = useMemo(() => {
      const allReports = [
        {
          token_id: null,
          name: 'None',
          status_color: 'EBEBEB',
          status_text_color: '73767D',
          status: 'rejected',
          status_formatted: 'rejected'
        },
        ...getUniqueListBy<ReportTiny>(
          [
            ...[transaction?.report ?? []],
            ...(filteredReports ?? [])
          ] as ReportTiny[],
          'token_id'
        )
      ]

      return allReports?.length
        ? (allReports.map(
            ({
              token_id,
              name,
              status_color,
              status_text_color,
              status_formatted
            }) => {
              return {
                token_id,
                label: (
                  <span className="flex items-center justify-between w-full h-[1.3rem]">
                    <span className="-ml-2">{name ?? 'Un-named report'}</span>
                    {token_id && (
                      <Status
                        bgColor={status_color}
                        textColor={status_text_color}
                        status={status_formatted}
                      />
                    )}
                  </span>
                )
              }
            }
          ) as MenuProps<ReportTiny>['options'])
        : [
            {
              token_id: 'empty',
              label: 'No reports yet. Add a report to get reimbursed easily',
              disabled: true
            }
          ]
    }, [filteredReports, transaction?.report])

    useEffect(() => {
      if (isDirty || !transaction) return
      reset({
        report_token_id: transaction?.report
          ? transaction?.report?.token_id
          : null,
        notes: transaction.notes,
        amount: numeral(Math.abs(transaction.amount)).format('$0,0.00'),
        date: transaction.date,
        merchant: transaction.merchant,
        category: transaction.category,
        tags: transaction.tags,
        photo: transaction.photo?.token_id ? transaction.photo : null,
        photo_attributes: null,
        purpose:
          transaction.purpose === null && transaction.income_source === null
            ? 'Unclassified'
            : (transaction.income_source ?? transaction.purpose)
      })
    }, [isDirty, reset, transaction])

    const todayInRange = useMemo(() => {
      if (!enableStartDate || !enableEndDate) return true
      return (
        enableStartDate &&
        enableEndDate &&
        dayjs().isBetween(dayjs(enableStartDate), dayjs(enableEndDate))
      )
    }, [enableEndDate, enableStartDate])

    useEffect(() => {
      if (isNewTransaction) {
        setValue(
          'purpose',
          isExpense ? (teamPurposes[0] ?? 'Personal') : 'Personal'
        )
        setValue(
          'date',
          !todayInRange
            ? dayjs(enableStartDate).format('YYYY-MM-DD')
            : dayjs().format('YYYY-MM-DD')
        )
        setValue('tags', [])
      }
    }, [
      isNewTransaction,
      isExpense,
      setValue,
      teamPurposes,
      todayInRange,
      enableStartDate
    ])

    return isLoading ? (
      <div className="flex justify-center gap-5 items-center w-full h-screen text-center">
        <Icon name="loading" size={24} />
        <p>Loading the {entityName}</p>
      </div>
    ) : (
      <FormProvider {...methods}>
        <div>
          {/*Locked banner*/}

          <div
            className={cn([
              'hidden mb-5 bg-dark-8 items-center gap-3 py-3 px-5 w-full',
              !!transaction?.is_locked && view === 'personal' && '!flex'
            ])}
          >
            <div>
              <LockFilledIcon color="#555555" height="25" width="25" />
            </div>
            <p>
              This {entityName} cannot be edited because it is either under
              review, approved, or paid. Contact your Team Manager to edit it.
            </p>
          </div>
          <div className="pb-20 px-6 pt-4">
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="flex items-center justify-between w-full">
                <p className="text-dark text-2xl font-semibold">{title}</p>
                <div className={cn(['flex gap-3', isReadOnly && 'hidden'])}>
                  <Button
                    variant="primary"
                    type="submit"
                    status={
                      updatingTransaction || creatingTransaction
                        ? 'loading'
                        : 'idle'
                    }
                    disabled={!isDirty}
                  >
                    Save
                  </Button>
                  {!isNewTransaction && (
                    <PopConfirm
                      onConfirm={onDelete}
                      title="Are you sure you want to delete this transaction?"
                    >
                      <Button
                        variant="outline"
                        icon="trash-outline"
                        className="text-danger"
                        status={deletingTransaction ? 'loading' : 'idle'}
                      />
                    </PopConfirm>
                  )}
                </div>
              </div>
            </form>

            {/*Value and attachment*/}
            <div className="grid grid-cols-12 items-center py-10 gap-5 border-b border-dark-2">
              <div className="w-full col-span-8 h-full flex items-center justify-center border-r border-dark-1">
                {isReadOnly ? (
                  <span
                    className={cn([
                      ' font-semibold',
                      amount?.toString().length > 8 ? 'text-4xl' : 'text-5xl',
                      amountNumber < 0 && 'text-danger'
                    ])}
                  >
                    {numeral(amount).format('$0,0.00')}
                  </span>
                ) : (
                  <SmartField
                    controllerProps={{
                      control,
                      name: 'amount',
                      rules: {
                        required: 'Specify the amount',
                        validate: (value) => {
                          return value !== '$0.00' || `Specify the amount`
                        }
                      }
                    }}
                    label=""
                  >
                    <Input
                      className={cn([
                        'outline-hidden! bg-transparent xs:text-4xl lg:text-4xl font-semibold',
                        'w-full ml:input-sm',
                        amountNumber < 0 && 'text-danger'
                      ])}
                      type="money"
                      placeholder="$0.00"
                      disabled={isReadOnly}
                    />
                  </SmartField>
                )}
              </div>
              <div className="col-span-4">
                <FilePicker
                  onChange={(file) => {
                    setValue('photo', null)
                    setValue('photo_attributes', file, {
                      shouldDirty: true
                    })
                  }}
                  value={photo}
                  disabled={isReadOnly}
                />
              </div>
            </div>
            {/*Form*/}
            <div className="mt-10 flex flex-col gap-5">
              <SmartField
                controllerProps={{
                  control,
                  name: 'purpose',
                  rules: {
                    required: 'Select a purpose',
                    validate: (value) => {
                      if (
                        hasTeam &&
                        !!report_token_id &&
                        !teamPurposes.includes(value as string)
                      ) {
                        notifications.error('Please select a work purpose')
                        return 'Please select a work purpose'
                      }
                    }
                  }
                }}
                orientation="horizontal"
                label={
                  <Icon
                    name="briefcase-outline"
                    size={25}
                    color="text-dark-4"
                  />
                }
                requirementDecoration={null}
              >
                <Select
                  placeholder="Choose purpose"
                  readOnly={isReadOnly}
                  variant="invisible"
                  className="w-full"
                  wrapperClassName="block!"
                  options={isPersonalReportPage ? teamPurposes : allPurposes}
                />
              </SmartField>

              <SmartField
                controllerProps={{
                  control,
                  name: 'merchant',
                  rules: {
                    required: 'Specify the merchant'
                  }
                }}
                orientation="horizontal"
                label={
                  <Icon name="shop-outline" size={25} color="text-dark-4" />
                }
                requirementDecoration={null}
              >
                <Input
                  placeholder="Merchant"
                  readOnly={isReadOnly}
                  className="w-full"
                  variant="invisible"
                />
              </SmartField>

              <SmartField
                controllerProps={{
                  control,
                  name: 'date',
                  rules: {
                    validate: {
                      inRange: (value) => {
                        if (!enableStartDate && !enableEndDate) return true
                        return (
                          dayjs(value as string).isBetween(
                            dayjs(enableStartDate),
                            dayjs(enableEndDate),
                            'days',
                            '[]'
                          ) || 'Date is outside of the report period'
                        )
                      }
                    }
                  }
                }}
                label={
                  <Icon name="calendar-outline" size={25} color="text-dark-4" />
                }
                orientation="horizontal"
              >
                <DatePicker
                  type="date-picker"
                  variant="invisible"
                  className="flex w-full"
                  wrapperClassName="block!"
                  hideIcon={true}
                  disableToday={!todayInRange}
                  disableAfter={enableEndDate ? dayjs(enableEndDate) : null}
                  disableBefore={
                    enableStartDate ? dayjs(enableStartDate) : null
                  }
                  disabled={isReadOnly}
                  readOnly={isReadOnly}
                />
              </SmartField>

              {categoryInputConfig.show && (
                <Tooltip
                  dark
                  position="left-middle"
                  triggerMode="click"
                  dismissOnMouseLeave
                  disabled={!categoryInputConfig.disabled}
                  content={
                    <div className="w-56">
                      Categories are no longer enabled on your team
                    </div>
                  }
                >
                  <SmartField
                    controllerProps={{
                      control,
                      name: 'category'
                    }}
                    orientation="horizontal"
                    label={
                      <Icon
                        name="categories-outline"
                        size={25}
                        color="text-dark-4"
                      />
                    }
                  >
                    <CategoryOption
                      view={view}
                      disabled={isReadOnly || categoryInputConfig.disabled}
                      readOnly={isReadOnly || categoryInputConfig.disabled}
                      wrapperClassName="block!"
                    />
                  </SmartField>
                </Tooltip>
              )}
              <SmartField
                controllerProps={{
                  control,
                  name: 'notes'
                }}
                orientation="horizontal"
                label={
                  <Icon name="note-outline" size={25} color="text-dark-4" />
                }
              >
                <Input
                  placeholder={isReadOnly ? 'No notes added' : 'Add a note...'}
                  readOnly={isReadOnly}
                  variant="invisible"
                  className="w-full"
                />
              </SmartField>

              {isExpense && hasTeam && (
                <SmartField
                  controllerProps={{
                    control,
                    name: 'report_token_id'
                  }}
                  orientation="horizontal"
                  label={
                    <Icon name="report-outline" size={25} color="text-dark-4" />
                  }
                >
                  <Select
                    readOnly={isReadOnly}
                    disabled={isReadOnly}
                    placeholder="Choose report"
                    wrapperClassName="block!"
                    className={cn([
                      'w-full !pr-2',
                      isReadOnly
                        ? 'text-dark'
                        : '!outline-0 hover:!outline-1 hover:!outline-primary'
                    ])}
                    options={reportOptions}
                    variant="invisible"
                  />
                </SmartField>
              )}

              <TagsInput
                readOnly={readOnly}
                tagsFromServer={transaction?.tags ?? []}
              />
            </div>
          </div>
        </div>
      </FormProvider>
    )
  }
)

export default TransactionDetail
